import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MM_LABEL } from "./mmConfig";

const P = 1;

interface Fund { id: string; fund_name: string; fund_manager: string | null; fund_class: string | null; isin: string | null; }
interface Weight { fund_id: string; weight_pct: number; }
interface Duration { fund_id: string; duration_years: number | null; updated_at: string | null; data_as_of: string | null; }

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};
const isStale = (iso: string | null) => {
  if (!iso) return false;
  const d = new Date(iso).getTime();
  return Date.now() - d > 31 * 24 * 60 * 60 * 1000;
};

export default function MmHoldingsDuration() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [weightEdits, setWeightEdits] = useState<Record<string, string>>({});

  const { data: funds } = useQuery({
    queryKey: ["ba-cbam-funds"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_funds").select("id, fund_name, fund_manager, fund_class, isin").order("fund_name");
      if (error) throw error;
      return (data || []) as unknown as Fund[];
    },
  });

  const { data: weights } = useQuery({
    queryKey: ["ba-mm-fund-weights", P],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_mm_cbam_fund_weights")
        .select("fund_id, weight_pct, effective_date")
        .eq("portfolio", P);
      if (error) throw error;
      const latest = new Map<string, Weight>();
      for (const r of (data || []) as any[]) {
        const prev = latest.get(r.fund_id);
        if (!prev || (r.effective_date > (prev as any).effective_date)) {
          latest.set(r.fund_id, r as Weight);
        }
      }
      return Array.from(latest.values());
    },
  });


  const { data: durations } = useQuery({
    queryKey: ["ba-mm-fund-durations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_mm_fund_durations" as any)
        .select("fund_id, duration_years, updated_at, data_as_of");
      if (error) throw error;
      return ((data || []) as unknown) as Duration[];
    },
  });

  const durationMap = useMemo(() => {
    const m = new Map<string, Duration>();
    for (const d of durations || []) m.set(d.fund_id, d);
    return m;
  }, [durations]);

  const rows = useMemo(() => {
    const wm = new Map<string, number>();
    for (const w of weights || []) wm.set(w.fund_id, Number(w.weight_pct));
    return (funds || [])
      .filter((f) => (wm.get(f.id) || 0) > 0)
      .map((f) => {
        const d = durationMap.get(f.id);
        return {
          ...f,
          weight: wm.get(f.id) || 0,
          duration: d?.duration_years ?? null,
          updated_at: d?.updated_at ?? null,
          data_as_of: d?.data_as_of ?? null,
        };
      });
  }, [funds, weights, durationMap]);

  const simWeight = (id: string, base: number) => {
    const raw = weightEdits[id];
    if (raw === undefined || raw === "") return base;
    const n = Number(raw);
    return Number.isNaN(n) ? base : n;
  };

  const totalSimWeight = useMemo(
    () => rows.reduce((acc, r) => acc + simWeight(r.id, r.weight), 0),
    [rows, weightEdits]
  );

  const weightedAvg = useMemo(() => {
    let num = 0, den = 0;
    for (const r of rows) {
      const d = edits[r.id] !== undefined ? Number(edits[r.id]) : r.duration;
      const w = simWeight(r.id, r.weight);
      if (d != null && !Number.isNaN(d)) { num += w * d; den += w; }
    }
    return den > 0 ? num / den : null;
  }, [rows, edits, weightEdits]);

  const [asOfEdits, setAsOfEdits] = useState<Record<string, string>>({});
  const [rebalNotes, setRebalNotes] = useState("");
  const [showRebal, setShowRebal] = useState(false);
  const [rebalDate, setRebalDate] = useState(new Date().toISOString().split("T")[0]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { fund_id: string; duration_years: number | null; data_as_of: string | null }) => {
      const { error } = await supabase
        .from("ba_mm_fund_durations" as any)
        .upsert({ ...payload, updated_at: new Date().toISOString() }, { onConflict: "fund_id" });
      if (error) throw error;
      const { snapshotMmFundDurations } = await import("./mmDurationSnapshot");
      await snapshotMmFundDurations("manual_edit", null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ba-mm-fund-durations"] });
      qc.invalidateQueries({ queryKey: ["ba-mm-duration-snapshots"] });
      toast({ title: "Saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const changedRows = useMemo(
    () =>
      rows
        .map((r) => {
          const target = simWeight(r.id, r.weight);
          const delta = +(target - r.weight).toFixed(4);
          return { ...r, target: +target.toFixed(4), delta };
        })
        .filter((r) => Math.abs(r.delta) >= 0.005),
    [rows, weightEdits]
  );

  const commitRebalanceMutation = useMutation({
    mutationFn: async () => {
      if (changedRows.length === 0) throw new Error("No weight changes to commit");
      if (Math.abs(totalSimWeight - 100) > 0.05) {
        throw new Error(`Target weights must sum to 100% (currently ${totalSimWeight.toFixed(2)}%)`);
      }
      const today = rebalDate || new Date().toISOString().split("T")[0];
      const batchId = (crypto as any).randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
      const rowsToInsert = changedRows.map((r) => ({
        trade_date: today,
        fund_id: r.id,
        direction: r.delta >= 0 ? "BUY" : "SELL",
        portfolio_1_included: true,
        portfolio_1_pct: Math.abs(r.delta),
        notes: rebalNotes || null,
        trade_type: "rebalance",
        batch_id: batchId,
      }));
      const { data: inserted, error: tErr } = await supabase
        .from("ba_mm_trades")
        .insert(rowsToInsert)
        .select("id");
      if (tErr) throw tErr;

      const weightUpserts = changedRows.map((r) => ({
        fund_id: r.id,
        portfolio: P,
        weight_pct: r.target,
        effective_date: today,
      }));
      const { error: wErr } = await supabase
        .from("ba_mm_cbam_fund_weights")
        .upsert(weightUpserts, { onConflict: "fund_id,portfolio,effective_date" });
      if (wErr) throw wErr;

      const { snapshotMmFundDurations } = await import("./mmDurationSnapshot");
      await snapshotMmFundDurations("trade", inserted?.[0]?.id ?? null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ba-mm-fund-weights", P] });
      qc.invalidateQueries({ queryKey: ["ba-mm-trades"] });
      qc.invalidateQueries({ queryKey: ["ba-mm-fund-weights-all"] });
      qc.invalidateQueries({ queryKey: ["ba-mm-duration-snapshots"] });
      setWeightEdits({});
      setRebalNotes("");
      setShowRebal(false);
      toast({ title: "Rebalance committed", description: `${changedRows.length} trades logged` });
    },
    onError: (e: any) => toast({ title: "Commit failed", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Holdings Duration</h1>
        <p className="text-muted-foreground text-sm">{MM_LABEL} — per-fund duration (days) and weighted-average portfolio duration</p>
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg">
            Weighted-average duration:{" "}
            <span className="font-mono">{weightedAvg != null ? `${weightedAvg.toFixed(2)} days` : "—"}</span>
            <span className="ml-3 text-xs text-muted-foreground font-normal">
              Sim total weight: <span className="font-mono">{totalSimWeight.toFixed(2)}%</span>
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {Object.keys(weightEdits).length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setWeightEdits({})}>
                Reset simulated weights
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const isCash = (r: typeof rows[number]) =>
                  r.fund_name.trim().toLowerCase() === "cash" &&
                  (r.fund_manager || "").trim().toLowerCase() === "cash";
                const cash = rows.find(isCash);
                const others = rows.filter((r) => !isCash(r));
                if (others.length === 0) return;
                const per = Math.floor(((100 - 0.5) / others.length) * 100) / 100;
                const next: Record<string, string> = {};
                let used = 0;
                for (const r of others) {
                  next[r.id] = per.toFixed(2);
                  used += per;
                }
                const cashWeight = Math.max(0.5, +(100 - used).toFixed(2));
                if (cash) next[cash.id] = cashWeight.toFixed(2);
                setWeightEdits(next);
              }}
              disabled={rows.length < 2}
            >
              Equal weight
            </Button>
            <Button
              size="sm"
              onClick={() => setShowRebal(true)}
              disabled={changedRows.length === 0}
            >
              Commit as Rebalance ({changedRows.length})
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Dialog open={showRebal} onOpenChange={setShowRebal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Commit Rebalance to Trade Log</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {changedRows.length} fund{changedRows.length === 1 ? "" : "s"} will be logged as a rebalance batch dated <span className="font-mono">{rebalDate}</span>.
              Target weights must sum to 100% (currently <span className="font-mono">{totalSimWeight.toFixed(2)}%</span>).
            </p>
            <div>
              <Label>Trade Date</Label>
              <Input type="date" value={rebalDate} onChange={(e) => setRebalDate(e.target.value)} className="w-48" />
            </div>
            <div className="max-h-72 overflow-y-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Fund</th>
                    <th className="text-right p-2">Current</th>
                    <th className="text-right p-2">Target</th>
                    <th className="text-right p-2">Δ</th>
                    <th className="text-center p-2">Dir</th>
                  </tr>
                </thead>
                <tbody>
                  {changedRows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="p-2">{r.fund_name}</td>
                      <td className="p-2 text-right font-mono">{r.weight.toFixed(2)}%</td>
                      <td className="p-2 text-right font-mono">{r.target.toFixed(2)}%</td>
                      <td className={`p-2 text-right font-mono ${r.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {r.delta >= 0 ? "+" : ""}{r.delta.toFixed(2)}%
                      </td>
                      <td className="p-2 text-center">{r.delta >= 0 ? "BUY" : "SELL"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={rebalNotes} onChange={(e) => setRebalNotes(e.target.value)} className="h-20" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRebal(false)}>Cancel</Button>
            <Button
              onClick={() => commitRebalanceMutation.mutate()}
              disabled={commitRebalanceMutation.isPending || Math.abs(totalSimWeight - 100) > 0.05}
            >
              {commitRebalanceMutation.isPending ? "Committing..." : "Commit Rebalance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground">Fund House</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund Class</th>
                <th className="text-left p-3 font-medium text-muted-foreground">ISIN</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Weight % (sim)</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Duration (days)</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Data as of</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Last updated</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No entries</td></tr>
              ) : rows.map((r, i) => {
                const val = edits[r.id] !== undefined ? edits[r.id] : (r.duration != null ? String(r.duration) : "");
                const wVal = weightEdits[r.id] !== undefined ? weightEdits[r.id] : r.weight.toFixed(2);
                const wDirty = weightEdits[r.id] !== undefined && Number(weightEdits[r.id]) !== r.weight;
                const asOfVal = asOfEdits[r.id] !== undefined ? asOfEdits[r.id] : (r.data_as_of || "");
                const stale = isStale(r.updated_at);
                return (
                  <tr key={r.id} className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                    <td className="p-3 text-xs text-muted-foreground">{r.fund_manager || "—"}</td>
                    <td className="p-3">{r.fund_name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{r.fund_class || "—"}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{r.isin || "—"}</td>
                    <td className="p-3 text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={wVal}
                        onChange={(e) => setWeightEdits({ ...weightEdits, [r.id]: e.target.value })}
                        className={`w-24 ml-auto text-right font-mono ${wDirty ? "border-primary" : ""}`}
                      />
                    </td>
                    <td className="p-3 text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={val}
                        onChange={(e) => setEdits({ ...edits, [r.id]: e.target.value })}
                        className="w-28 ml-auto text-right"
                      />
                    </td>
                    <td className="p-3">
                      <Input
                        type="date"
                        value={asOfVal}
                        onChange={(e) => setAsOfEdits({ ...asOfEdits, [r.id]: e.target.value })}
                        className="w-40"
                      />
                    </td>
                    <td className="p-3 text-xs">
                      {stale ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 text-amber-600 cursor-help">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                {fmtDate(r.updated_at)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Not updated in over a month</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-muted-foreground">{fmtDate(r.updated_at)}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const raw = edits[r.id];
                          const num = raw === "" || raw === undefined ? null : Number(raw);
                          const asOf = asOfEdits[r.id] !== undefined ? asOfEdits[r.id] : r.data_as_of;
                          saveMutation.mutate({ fund_id: r.id, duration_years: num, data_as_of: asOf || null });
                        }}
                      >
                        Save
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
